import { Navigate } from 'react-router-dom'
import { useAuth, canAccess, canView } from '@/hooks/useAuth'
import {Loader2, ShieldX} from 'lucide-react'
import type { UserModuleAccess } from '@/types'

interface ProtectedRouteProps {
  children: React.ReactNode
  /**
   * 'any'    = qualquer utilizador autenticado
   * 'admin'  = apenas administradores
   * módulo   = utilizador admin OU utilizador com acesso view ao módulo
   */
  require?: 'any' | 'admin' | UserModuleAccess
}

export default function ProtectedRoute({ children, require = 'any' }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isAdmin, user } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Loader2 size={28} className="animate-spin" style={{ color: '#5BBDB5' }} />
          <span className="text-sm">A verificar sessão...</span>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />
  }

  // Administrador tem acesso total a tudo
  if (isAdmin) return <>{children}</>

  // require='any' → qualquer utilizador autenticado
  if (require === 'any') return <>{children}</>

  // require='admin' → apenas admins (utilizadores comuns são barrados)
  if (require === 'admin') {
    return (
      <AccessDenied message="É necessário nível de Administrador para aceder esta área." />
    )
  }

  // require=módulo → verifica se o utilizador tem pelo menos view no módulo
  const modKey = require as UserModuleAccess
  if (canAccess(user, modKey)) {
    return <>{children}</>
  }

  return (
    <AccessDenied message={`Você não tem permissão para aceder este módulo.`} />
  )
}

function AccessDenied({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-8">
      <div className="text-center max-w-sm">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: '#FEF2F2' }}
        >
          <ShieldX size={24} className="text-red-500" />
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
          Acesso Negado
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
      </div>
    </div>
  )
}
